/**
 * Available Templates Store - Zustand State Management
 * 
 * Manages available category templates state for users.
 * Allows users to view and add templates they don't currently have.
 * 
 * @validates Requirements 6.1, 6.2, 6.3 - Template availability and re-addition
 */

import { create } from 'zustand';
import { CategoryTemplate, Category } from '@budgira/shared';
import { templateService } from '../services/template.service';
import i18n from '../i18n';

interface AvailableTemplatesState {
  templates: CategoryTemplate[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchAvailableTemplates: () => Promise<void>;
  addTemplate: (templateId: string) => Promise<Category[] | null>;
  clearError: () => void;
}

/**
 * Map backend error codes to localized messages
 */
function localizeTemplateError(err: unknown): string {
  // Extract error code from API response
  let errorCode: string | undefined;
  let errorMessage: string | undefined;

  if (err && typeof err === 'object') {
    // Axios error structure
    const axiosError = err as { response?: { data?: { error?: { code?: string; message?: string } } } };
    errorCode = axiosError.response?.data?.error?.code;
    errorMessage = axiosError.response?.data?.error?.message;
  }

  // Map error codes to translation keys
  const errorKeyMap: Record<string, string> = {
    'TEMPLATE_NOT_FOUND': 'availableTemplates.errors.notFound',
    'TEMPLATE_ALREADY_ADDED': 'availableTemplates.errors.alreadyAdded',
  };

  if (errorCode && errorKeyMap[errorCode]) {
    const key = errorKeyMap[errorCode]!;
    return String(i18n.t(key));
  }

  // Fallback to backend message or generic error
  return errorMessage || String(i18n.t('availableTemplates.errors.generic'));
}

export const useAvailableTemplatesStore = create<AvailableTemplatesState>((set, get) => ({
  templates: [],
  isLoading: false,
  error: null,

  /**
   * Fetch available templates for the current user
   * @validates Requirement 6.1, 6.2
   */
  fetchAvailableTemplates: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const response = await templateService.getAvailableTemplates();
      if (response.success && response.data) {
        set({ templates: response.data, isLoading: false });
      } else {
        set({ 
          error: response.error?.message || String(i18n.t('availableTemplates.errors.generic')), 
          isLoading: false 
        });
      }
    } catch (err) {
      const message = localizeTemplateError(err);
      set({ error: message, isLoading: false });
    }
  },

  /**
   * Add a template to the user's categories
   * @validates Requirement 6.3
   */
  addTemplate: async (templateId: string): Promise<Category[] | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await templateService.addTemplate(templateId);
      if (response.success && response.data) {
        // Refresh available templates to update the list
        await get().fetchAvailableTemplates();
        return response.data;
      } else {
        set({ 
          error: response.error?.message || String(i18n.t('availableTemplates.errors.generic')), 
          isLoading: false 
        });
        return null;
      }
    } catch (err) {
      const message = localizeTemplateError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  clearError: () => set({ error: null }),
}));

export default useAvailableTemplatesStore;
